import { useEffect, useId, useRef } from 'react';
import type { ClientFields } from './client-directory-api';
import { PrototypeIcon } from './PrototypeIcon';

/** L'ancien scalaire reste lisible pendant une livraison ou dans un onglet déjà ouvert. */
export function clientWebsiteUrls(
  client: Pick<ClientFields, 'websiteUrl' | 'websiteUrls'>,
): readonly string[] {
  return client.websiteUrls ?? (client.websiteUrl ? [client.websiteUrl] : []);
}

export function ClientWebsitesFields({
  values,
  onChange,
}: {
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
}): React.JSX.Element {
  const helpId = useId();
  const fieldset = useRef<HTMLFieldSetElement>(null);
  const focusAfterChange = useRef<number | null>(null);
  const rows = values.length ? values : [''];

  useEffect(() => {
    if (focusAfterChange.current === null) return;
    fieldset.current?.querySelectorAll('input')[focusAfterChange.current]?.focus();
    focusAfterChange.current = null;
  }, [values]);

  return (
    <fieldset ref={fieldset} className="client-directory__websites client-directory__wide">
      <legend>Sites internet</legend>
      <p id={helpId}>
        Ajoutez les adresses des sites de ce client. Ces informations sont facultatives.
      </p>
      {rows.map((value, index) => (
        <div className="client-directory__website-row" key={index}>
          <label>
            <span>Site internet {index + 1}</span>
            <input
              type="text"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={2048}
              value={value}
              aria-describedby={helpId}
              placeholder="exemple.fr ou https://exemple.fr"
              onChange={(event) => {
                const address = event.currentTarget.value;
                onChange(rows.map((current, position) => (position === index ? address : current)));
              }}
            />
          </label>
          <button
            type="button"
            aria-label={`Supprimer le site internet ${index + 1}`}
            onClick={() => {
              focusAfterChange.current = Math.max(0, Math.min(index, rows.length - 2));
              onChange(rows.filter((_, position) => position !== index));
            }}
          >
            <PrototypeIcon name="x" />
            <span>Supprimer</span>
          </button>
        </div>
      ))}
      <button
        type="button"
        className="client-directory__website-add"
        disabled={rows.length >= 50}
        onClick={() => {
          focusAfterChange.current = rows.length;
          onChange([...rows, '']);
        }}
      >
        <PrototypeIcon name="plus" /> Ajouter un site
      </button>
    </fieldset>
  );
}
